import {
  GetQBankStructureLevelItemsByQBankStructureIdAndUserIdDocument,
  QBankStructureLevelCategory,
  SearchUsersContentVisibilityDocument,
  useGetQBankStructureLevelItemsByQBankStructureIdAndUserIdQuery,
  useUpdateUserToQBankStructureMutation,
} from '@exam-management/graphql';
import { APP_PERMISSIONS } from '@exam-management/permissions';
import {
  levelItemsToContentType,
  type QBankStructureCVForm,
} from '@exam-management/qbank-structure-cv-list';
import { QBankStructureCvListWithSelectedUsersForm } from '@exam-management/qbank-structure-cv-list-with-selected-users-form';
import { useQBankStructure } from '@exam-management/qbs-provider';
import { useParams } from '@exam-management/router';
import { type UserRow } from '@exam-management/user-management';
import { zodResolver } from '@hookform/resolvers/zod';
import { useAuth } from '@shared/auth';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Form,
  Loading,
  Text,
} from '@shared/components';
import { useHasPermission } from '@shared/permissions';
import { useSnackbar } from '@shared/snackbar';
import { useEffect, useMemo } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { z } from 'zod';

const EditUsersToQBankStructureFormSchema = z.object({
  userIds: z.array(z.string()).length(1),
  contentIds: z.record(
    z.string(),
    z.object({
      isVisible: z.boolean(),
      type: z.nativeEnum(QBankStructureLevelCategory),
      parentLevelItemId: z.string().nullable(),
    }),
  ),
});

type EditUsersToQBankStructureFormInputs = z.infer<typeof EditUsersToQBankStructureFormSchema>;

interface EditUserContentVisibilityProps {
  userDetails: UserRow;
  open: boolean;
  onClose: () => void;
}

export default function EditUserContentVisibility(props: Readonly<EditUserContentVisibilityProps>) {
  const { open: isOpen, onClose, userDetails } = props;
  const { structureId } = useParams('/qbank-structures/:structureId');
  const { userProfile } = useAuth();

  const { qBankStructureInformation, qBankStructureLevelItems, qBankStructureLevels } =
    useQBankStructure();

  const isPartLevelDisabled = qBankStructureLevels?.some(
    (a) => a.category === QBankStructureLevelCategory.Part && !a.enabled,
  );

  const isSubjectLevelDisabled = qBankStructureLevels?.some(
    (a) => a.category === QBankStructureLevelCategory.Subject && !a.enabled,
  );

  const isPartAndSubjectLevelDisabled = isPartLevelDisabled && isSubjectLevelDisabled;

  const courseId = useMemo(
    () =>
      qBankStructureLevelItems.find(
        (a) => a.qBankStructureLevel.category === QBankStructureLevelCategory.Course,
      )?.id ?? '',
    [qBankStructureLevelItems],
  );

  const { showSnackbar } = useSnackbar();

  const input = {
    qBankStructureId: structureId,
    userId: userDetails.userId,
    options: {
      categories: [
        QBankStructureLevelCategory.Part,
        QBankStructureLevelCategory.Subject,
        QBankStructureLevelCategory.Group,
        QBankStructureLevelCategory.Chapter,
        QBankStructureLevelCategory.Topic,
        QBankStructureLevelCategory.SubTopic,
      ],
    },
  };
  const [updateUsersToQBankStructure] = useUpdateUserToQBankStructureMutation({
    onCompleted: (res) => {
      if (res?.qbsUpdateUsersQBankStructureLevelItemContentVisibility) {
        showSnackbar({
          message: 'Visibility has been saved',
          variant: 'success',
        });
      }
      handleClose();
    },
    onError: () => {
      showSnackbar({
        message: 'Visibility is unable to save',
        variant: 'error',
      });
    },
    refetchQueries: [
      {
        query: GetQBankStructureLevelItemsByQBankStructureIdAndUserIdDocument,
        variables: input,
      },
      SearchUsersContentVisibilityDocument,
    ],
    awaitRefetchQueries: true,
  });

  const { data: getUserCV, loading: isLoading } =
    useGetQBankStructureLevelItemsByQBankStructureIdAndUserIdQuery({
      variables: input,
      fetchPolicy: 'cache-and-network',
    });

  const userCV = useMemo(
    () => getUserCV?.qbsGetQBankStructureLevelItemsByQBankStructureIdAndUserId ?? [],
    [getUserCV],
  );

  const formContext = useForm<EditUsersToQBankStructureFormInputs>({
    defaultValues: {
      userIds: [userDetails.userId],
      contentIds: {},
    },
    resolver: zodResolver(EditUsersToQBankStructureFormSchema),
  });

  const {
    formState: { isDirty: isFormDirty, isSubmitting },
    handleSubmit,
  } = formContext;

  const handleClose = () => {
    onClose();
    formContext.reset();
  };

  const initialCvValues = useMemo(() => {
    const result: QBankStructureCVForm['contentIds'] = {};
    for (const cv of userCV) {
      result[cv.id] = {
        isVisible: cv.isVisible,
        type: cv.type as QBankStructureLevelCategory,
        parentLevelItemId: cv.parentLevelItemId,
      };
    }
    return result;
  }, [userCV]);

  const onSubmit = handleSubmit(async (data) => {
    const contentVisibilityIds = [];

    if (isPartAndSubjectLevelDisabled) {
      for (const [key, value] of Object.entries(data?.contentIds ?? {})) {
        contentVisibilityIds.push({
          contentId: key,
          parentContentId: value.parentLevelItemId,
          contentType: levelItemsToContentType[value.type],
        });
      }
    } else {
      for (const [key, value] of Object.entries(data?.contentIds ?? {})) {
        if (value.isVisible) {
          contentVisibilityIds.push({
            contentId: key,
            parentContentId: value.parentLevelItemId,
            contentType: levelItemsToContentType[value.type],
          });
        }
      }
    }

    await updateUsersToQBankStructure({
      variables: {
        input: {
          userIds: data.userIds,
          visibleContent: contentVisibilityIds,
          qbankStructureId: structureId,
          courseId,
        },
      },
    });
  });

  useEffect(() => {
    formContext.reset({
      userIds: [userDetails.userId],
      contentIds: initialCvValues,
    });
  }, [initialCvValues, isOpen]);

  const hasUpdatePermission = useHasPermission(APP_PERMISSIONS.QBANK.SETTING.UPDATE);
  const isQBankStructureOwner = qBankStructureInformation?.createdBy === userDetails?.userId;
  const isMyCV = userProfile?.id === userDetails?.userId;
  const isEditable = hasUpdatePermission && !isQBankStructureOwner && !isMyCV;

  return (
    <Dialog open={isOpen} onClose={handleClose} maxWidth='xl'>
      <DialogTitle onClose={handleClose}>Visibility Settings</DialogTitle>
      <Divider />
      {!isLoading ? (
        <FormProvider {...formContext}>
          <Form onSubmit={onSubmit}>
            <DialogContent
              sx={{ display: 'flex', flexDirection: 'column', gap: (theme) => theme.spacing(4) }}
            >
              <Text variant='h4'>Selected User</Text>
              <QBankStructureCvListWithSelectedUsersForm
                usersDetails={[userDetails]}
                disabled={!isEditable}
              />
            </DialogContent>
            <Divider />
            <DialogActions sx={{ justifyContent: 'space-between' }}>
              <Button
                onClick={handleClose}
                variant='outlined'
                color='secondary'
                disabled={isSubmitting}
              >
                Close
              </Button>
              <Button type='submit' disabled={!isEditable || !isFormDirty} loading={isSubmitting}>
                Confirm
              </Button>
            </DialogActions>
          </Form>
        </FormProvider>
      ) : (
        <Loading sx={{ height: '100vh' }} />
      )}
    </Dialog>
  );
}
